import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

export interface ToastOptions {
  message: string;
  duration?: number;
  color?: 'success' | 'danger' | 'warning' | 'info' | 'primary' | string;
  actionText?: string;
  onAction?: () => void;
}

@Injectable({
  providedIn: 'root',
})
export class BaseHelper {
  private containerEl: HTMLElement | null = null;

  constructor(private router: Router) {
    this.ensureContainer();
  }

  private ensureContainer(): HTMLElement {
    if (this.containerEl && document.body.contains(this.containerEl)) {
      return this.containerEl;
    }

    let existing = document.getElementById('global-toast-container');
    if (!existing) {
      existing = document.createElement('div');
      existing.id = 'global-toast-container';
      existing.className = 'global-toast-container';
      document.body.appendChild(existing);
    }
    this.containerEl = existing;
    return this.containerEl;
  }

  /**
   * Display a floating feedback toast matching the reference design
   * @param message Message or error description
   * @param duration Milliseconds to display (default: 4000ms)
   * @param color 'success' | 'danger' | 'warning' | 'info' | 'primary'
   * @param actionText Optional action button text (e.g. 'Dismiss')
   */
  toast(
    message: string,
    duration: number = 4000,
    color: 'success' | 'danger' | 'warning' | 'info' | 'primary' | string = 'success',
    actionText?: string,
    onAction?: () => void
  ): Promise<void> {
    return new Promise((resolve) => {
      let toastColor = color || 'success';
      if (toastColor === 'green') toastColor = 'success';
      if (toastColor === 'red' || toastColor === 'error') toastColor = 'danger';

      const container = this.ensureContainer();

      const toastItem = document.createElement('div');
      toastItem.className = `global-toast-item toast-${toastColor}`;

      // Icon determination
      let iconSvg = '';
      if (toastColor === 'danger') {
        iconSvg = `<svg class="toast-ico" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>`;
      } else if (toastColor === 'warning') {
        iconSvg = `<svg class="toast-ico" viewBox="0 0 24 24" fill="currentColor"><path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/></svg>`;
      } else {
        iconSvg = `<svg class="toast-ico" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>`;
      }

      toastItem.innerHTML = `
        <div class="toast-content">
          ${iconSvg}
          <span class="toast-msg">${this.escapeHtml(message)}</span>
        </div>
        <div class="toast-actions">
          ${actionText ? `<button class="toast-btn-action">${this.escapeHtml(actionText)}</button>` : ''}
          <button class="toast-btn-close" aria-label="Close">✕</button>
        </div>
      `;

      container.appendChild(toastItem);

      // Trigger entrance animation
      requestAnimationFrame(() => {
        toastItem.classList.add('show');
      });

      let timeoutId: any = null;

      const dismiss = () => {
        if (timeoutId) clearTimeout(timeoutId);
        toastItem.classList.remove('show');
        toastItem.classList.add('hide');
        setTimeout(() => {
          if (toastItem.parentNode) {
            toastItem.parentNode.removeChild(toastItem);
          }
          resolve();
        }, 220);
      };

      const closeBtn = toastItem.querySelector('.toast-btn-close');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => dismiss());
      }

      const actBtn = toastItem.querySelector('.toast-btn-action');
      if (actBtn) {
        actBtn.addEventListener('click', () => {
          if (onAction) onAction();
          dismiss();
        });
      }

      if (duration > 0) {
        timeoutId = setTimeout(() => {
          dismiss();
        }, duration);
      }
    });
  }

  /**
   * Convenience helpers
   */
  success(message: string, duration = 3500) {
    return this.toast(message, duration, 'success');
  }

  danger(message: string, duration = 5000) {
    return this.toast(message, duration, 'danger');
  }

  error(message: string, duration = 5000) {
    return this.toast(message, duration, 'danger');
  }

  warning(message: string, duration = 4000) {
    return this.toast(message, duration, 'warning');
  }

  info(message: string, duration = 4000) {
    return this.toast(message, duration, 'info');
  }

  navigate(url: string, extras?: any) {
    this.router.navigate([url], extras);
  }

  formatDateTime(dateVal: any): string {
    if (!dateVal) return '—';
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return '—';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const strHours = String(hours).padStart(2, '0');
    return `${day}/${month}/${year}, ${strHours}:${minutes} ${ampm}`;
  }

  formatDate(dateVal: any): string {
    if (!dateVal) return '—';
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return '—';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }

  private escapeHtml(str: string): string {
    return (str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}
